<template>
    <div class="ui-page">
        <ui-page-header title="Assembleias" subtitle="Assembleias de credores dos casos, com sessões, credenciamento e votações.">
            <ui-action-menu :items="[
                { text: 'Central de homologação', icon: 'mdi-clipboard-check-outline', to: '/agcs-homologacoes' },
                { text: 'Substabelecimentos', icon: 'mdi-account-switch-outline', to: '/agcs-substabelecimentos' },
            ]" />
            <v-btn v-if="$allowed('create')" id="btnNew" color="primary" depressed to="/agcs/novo">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <!-- Visao por caso: convocacoes e prosseguimentos agrupados, com instalacao e resultados -->
        <ui-section title="Assembleias por caso">
            <template v-if="caseView.clientId" #actions>
                <v-btn small text color="primary" :loading="caseView.loading" @click="loadCaseSessions">
                    <v-icon left small>mdi-refresh</v-icon> Atualizar
                </v-btn>
            </template>

            <v-row>
                <v-col cols="12" md="6">
                    <v-autocomplete dense outlined hide-details placeholder="Selecione o caso" v-model="caseView.clientId"
                        :items="caseView.clients" item-value="id" item-text="name" :loading="caseView.loadingClients"
                        :menu-props="{ offsetY: true }" clearable @change="loadCaseSessions"></v-autocomplete>
                </v-col>
            </v-row>

            <v-progress-linear v-if="caseView.loading" indeterminate class="mt-4"></v-progress-linear>
            <v-alert v-else-if="caseView.clientId && !caseView.groups.length" dense text type="info" class="mt-4 mb-0">
                Nenhuma assembleia cadastrada para este caso.
            </v-alert>

            <div v-for="group in caseView.groups" :key="group.root_id" class="case-group mt-4">
                <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                    <span class="ui-cell-main">{{ group.convocation_label }} · {{ group.root_date | defaultDate("DD/MM/YYYY", false, true) }}</span>
                    <span class="ui-small ui-muted">{{ group.root_name }}</span>
                    <v-spacer></v-spacer>
                    <ui-badge v-if="group.installed_in" tone="success">Instalada na sessão "{{ group.installed_in.label }}"</ui-badge>
                    <ui-badge v-else tone="neutral" text="Não instalada" />
                </div>
                <v-simple-table dense class="mt-2">
                    <thead>
                        <tr>
                            <th class="text-left">Sessão</th>
                            <th class="text-left">Data (BRT)</th>
                            <th class="text-left">Status</th>
                            <th class="text-left">Instalação</th>
                            <th class="text-left">Credenciamento</th>
                            <th class="text-right">Cred. / Pres.</th>
                            <th class="text-left">Votações e resultados</th>
                            <th class="text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="session in group.sessions" :key="session.id">
                            <td>
                                <div class="ui-cell-main">{{ session.session_label }}</div>
                                <div class="ui-cell-sub">{{ session.name }}</div>
                            </td>
                            <td>{{ session.date | defaultDate("DD/MM/YYYY HH:mm", false, true) }}</td>
                            <td>
                                <ui-badge :text="getStatusDescription(session.status)" :tone="getStatusTone(session.status)" />
                            </td>
                            <td>
                                <ui-badge v-if="session.installed_here" tone="success" text="Instalada aqui" />
                                <span v-else-if="session.installation_inherited_from" class="ui-small">Herdada de "{{ session.installation_inherited_from.label }}"</span>
                                <span v-else class="ui-small ui-muted">—</span>
                            </td>
                            <td>
                                <v-tooltip bottom>
                                    <template #activator="{ on }">
                                        <span v-on="on">
                                            <ui-badge
                                                :tone="session.accreditation_availability && session.accreditation_availability.is_open ? 'success' : (session.continuation_locked ? 'warning' : 'neutral')"
                                                :text="session.continuation_locked ? 'Só substabelecimento' : (session.accreditation_availability && session.accreditation_availability.is_open ? 'Aberto' : 'Fechado')" />
                                        </span>
                                    </template>
                                    <span>{{ session.accreditation_availability ? session.accreditation_availability.message : '' }}</span>
                                </v-tooltip>
                            </td>
                            <td class="text-right">
                                {{ session.accredited_creditors }} / {{ session.present_creditors }}
                                <span v-if="session.eligible_creditors !== null" class="ui-small ui-muted">(aptos: {{ session.eligible_creditors }})</span>
                            </td>
                            <td>
                                <div v-if="session.votings.length" class="d-flex flex-column" style="gap: 2px;">
                                    <div v-for="voting in session.votings" :key="voting.id" class="d-flex align-center" style="gap: 6px;">
                                        <span class="ui-small">{{ voting.name }}</span>
                                        <ui-badge v-if="voting.result" :text="voting.result" :tone="voting.result === 'Aprovado' ? 'success' : (voting.result === 'Empate' ? 'warning' : 'danger')" />
                                        <span v-else class="ui-small ui-muted">{{ votingStatusLabel(voting.status) }}</span>
                                    </div>
                                </div>
                                <span v-else class="ui-small ui-muted">Sem votações</span>
                            </td>
                            <td class="text-right">
                                <ui-row-actions :actions="[
                                    { text: 'Abrir assembleia', icon: 'mdi-pencil-outline', handler: () => $router.push(`/agcs/${session.id}`) },
                                    { text: 'Lista de presença', icon: 'mdi-account-check-outline', handler: () => $router.push(`/agcs/presenca/${session.id}`) },
                                    { text: 'Cenários e votações', icon: 'mdi-vote-outline', handler: () => $router.push(`/agcs/${session.id}/cenarios`) },
                                ]" />
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>
        </ui-section>

        <h3 class="ui-section-title mt-6 mb-3">Busca em todas as assembleias</h3>

        <ui-table-card :more-count="moreFiltersCount" clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="dataList.filter.name" placeholder="Nome da assembleia" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.client_name" placeholder="Caso"
                    dense outlined hide-details class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-select v-model="dataList.filter.period" :items="searchOptions" item-value="id" item-text="name" placeholder="Período"
                    dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
                <v-select v-model="dataList.filter.status" :items="statusOptions" item-value="id" item-text="name" placeholder="Status"
                    dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
            </template>
            <template #more>
                <v-text-field v-model="dataList.filter.date" type="date" placeholder="Data"
                    dense outlined hide-details class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.time" type="time" placeholder="Hora"
                    dense outlined hide-details class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhuma assembleia foi encontrada" loading-text="Carregando dados..." class="ui-clickable-rows"
                @click:row="editItem">
                <template v-slot:[`item.name`]="{ item }">
                    <span class="ui-cell-main">{{ item.name }}</span>
                </template>

                <template v-slot:[`item.date`]="{ item }">
                    {{ item.date | defaultDate("DD/MM/YYYY HH:mm [BRT]", false, true) }}
                </template>

                <template v-slot:[`item.client.name`]="{ item }">
                    {{ item.client ? item.client.name : 'N/A' }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="getStatusDescription(item.status)" :tone="getStatusTone(item.status)" />
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => editItem(item) , disabled: !$allowed('edit') },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, handler: () => deleteItem(item) , disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>
    </div>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import object from "@/common/util/object";

export default {
    name: "GeneralMeetingList",

    data: () => ({
	        searchOptions: [
	            { id: "", name: "Todas as assembleias" }, 
	            { id: "next", name: "Próximas" }, 
	            { id: "previous", name: "Anteriores" }
	        ],
	        // Visao por caso
	        caseView: {
	            clientId: null,
	            clients: [],
	            loadingClients: false,
	            loading: false,
	            groups: [],
	        },
	        statusOptions: [
	            { id: "", name: "Todos" },
	            { id: 0, name: "Aberta" },
	            { id: 1, name: "Fechada" },
	            { id: 2, name: "Interrompida" },
	            { id: 3, name: "Suspensa" },
	            { id: 4, name: "Finalizada" },
	            { id: 5, name: "Em andamento" },
	        ],

	        dataList: {
	            filter: {
	                name: "",
	                client_name: "",
	                status: "",
	                date: "",
	                time: "",
	                period: "" 
	            },
            headers: [
                { text: "ID", value: "id" },
                { text: "Nome", value: "name" },
                { text: "Data (BRT)", value: "date" },
                { text: "Caso", value: "client.name" },
                { text: "Status", value: "status" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
                sortDesc: [false],
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            }
        },
    }),

    computed: {
        // Filtros escondidos em "Mais filtros" que estao preenchidos.
        moreFiltersCount() {
            return [this.dataList.filter.date, this.dataList.filter.time].filter((value) => value !== null && value !== undefined && value !== "" && value !== false).length;
        },
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value !== "" && value !== null && value !== undefined);
        },
    },

    created() {
        this.loadCaseClients();
        if (this.$route.query.client_id) {
            this.caseView.clientId = Number(this.$route.query.client_id);
            this.loadCaseSessions();
        }
        this.load();
    },

    methods: {
        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataList.filter = { name: "", client_name: "", status: "", date: "", time: "", period: "" };
            this.applyFilters();
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        pageSizeChanged(size) {
            this.dataList.options.itemsPerPage = size;
            this.load(true);
        },

	        clearFilter() {
	            this.dataList.filter.name = "";
	            this.dataList.filter.client_name = "";
	            this.dataList.filter.status = "";
	            this.dataList.filter.date = "";
	            this.dataList.filter.time = "";
	            this.dataList.filter.period = "";
	            this.dataList.options.page = 1;
	            this.load();
        },

        async load(resetPage = false) {
            if (resetPage) this.dataList.options.page = 1;
            
            this.dataList.loading = true;
            
	            const { period, ...filters } = this.dataList.filter;
	            let search = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== null && v !== undefined));
	            search = !object.isEmpty(search) ? search : {};

            const limit = this.dataList.options.itemsPerPage;
            const page = this.dataList.options.page;

	            let endpoint = '/api/general-meetings';

	            if (period === 'next') {
	                endpoint = '/api/general-meetings/next';
	            } else if (period === 'previous') {
	                endpoint = '/api/general-meetings/previous';
	            }

            try {
                const result = await axios.get(endpoint, { 
                    params: { page: page, limit: limit, search: search } 
                });

                if (result.data.content.data) {
                    this.dataList.data = result.data.content.data;
                    this.dataList.count = result.data.content.paging ? result.data.content.paging.total : result.data.content.data.length;
                } else {
                    this.dataList.data = result.data.content;
                    this.dataList.count = result.data.count || result.data.content.length;
                }

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        editItem(item) {
            this.$router.push(`/agcs/${item.id}`);
        },

        async deleteItem(item) {
            if (!item || !item.id) return;

            const proceedAction = await this.$dialog.confirm({
                text: `Deseja excluir a assembleia <strong>${item.name}</strong>? Ela será removida da listagem e do calendário do site.`,
                title: 'Excluir Assembleia',
                actions: {
                    false: 'Cancelar',
                    true: 'Excluir'
                }
            });

            if (!proceedAction) return;

            this.dataList.loading = true;

            try {
                await axios.delete(`/api/admin/general-meetings/${item.id}`);
                this.$dialog.notify.info("Assembleia excluída com sucesso", { position: "top-right", timeout: 2000 });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        async loadCaseClients() {
            this.caseView.loadingClients = true;
            try {
                const result = await axios.get('/api/clients', { params: { start: 1, limit: 100000, search: {} } });
                this.caseView.clients = result.data.content.data || [];
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 3000 });
            } finally {
                this.caseView.loadingClients = false;
            }
        },

        async loadCaseSessions() {
            if (!this.caseView.clientId) { this.caseView.groups = []; return; }
            this.caseView.loading = true;
            try {
                const result = await axios.get('/api/admin/general-meetings/sessions', { params: { client_id: this.caseView.clientId } });
                this.caseView.groups = (result.data.content && result.data.content.groups) || [];
            } catch (error) {
                this.caseView.groups = [];
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 4000 });
            } finally {
                this.caseView.loading = false;
            }
        },

        votingStatusLabel(status) {
            return { 1: 'criada', 2: 'em andamento', 3: 'pausada', 4: 'finalizada', 5: 'publicada' }[Number(status)] || '';
        },

        getStatusTone(status) {
            return { 0: 'primary', 1: 'neutral', 2: 'danger', 3: 'warning', 4: 'success', 5: 'info' }[status] || 'neutral';
        },

        getStatusBg(status) {
            switch (status) {
                case 0: return '#EEF2FF'
                case 1: return '#F3F4F6'
                case 2: return '#FEF2F2'
                case 3: return '#FFFBEB'
                case 4: return '#F0FDF4'
                case 5: return '#EFF6FF'
                default: return '#F3F4F6'
            }
        },

        getStatusText(status) {
            switch (status) {
                case 0: return '#1E1E2D'
                case 1: return '#6B7280'
                case 2: return '#DC2626'
                case 3: return '#D97706'
                case 4: return '#16A34A'
                case 5: return '#2563EB'
                default: return '#6B7280'
            }
        },

        getStatusDescription(status) {
            switch (status) {
                case 0: return 'Aberta'
                case 1: return 'Fechada'
                case 2: return 'Interrompida'
                case 3: return 'Suspensa'
                case 4: return 'Finalizada'
                case 5: return 'Em andamento'
                default: return 'Desconhecido'
            }
        },

        async generatePdf(item) {

            if (!item || !item.id) return;

            const proceedAction = await this.$dialog.confirm({
                text: 'Gerar PDF da assembleia <strong>' + item.name + '</strong> ?',
                title: 'Gerar relatório final',
                actions: {
                    false: 'Cancelar',
                    true: 'Gerar PDF'
                }
            })

            if (!proceedAction) return;

            console.log("gerando pdf da assembleia")

            this.dataset.loading = true;


            try {
                const response = await axios.post(`/api/admin/general-meetings/generate-pdf/${item.id}`, {}, { responseType: 'blob' })


                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                // Create a link element to trigger the download
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'file_new.pdf');

                // Append the link to the body and click it to start the download
                document.body.appendChild(link);
                link.click();

                // Clean up by revoking the URL object
                window.URL.revokeObjectURL(url);

            } catch (error) {
                console.error('Failed to generate PDF:', error)

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            } finally {
                this.dataset.loading = false;
            }




            // try {

            //     const result = await axios.delete(`/api/admin/general-meetings/${item.id}`)

            //     this.$dialog.notify.info("Assembleia excluída com sucesso", { position: "top-right", timeout: 2000 });              

            // } catch (error) {

            //     this.$dialog.notify.error(this.$codes(error), {
            //         position: 'top-right',
            //         timeout: 2000
            //     })

            // } finally {
            //     this.dataset.loading = false;
            // }

        },


        assemblySearch() {

            // if (!this.dataset.search && this.dataset.assemblySearch !== '' ) {
            //     this.$dialog.notify.error("Informe dados para pesquisa!", { position: "top-right", timeout: 2000 });

            //     return;
            // }

            const { assemblySearch } = this.dataset
            
            if (assemblySearch === 1) {

                this.dataset.filter = {name: this.dataset.search }
                this.dataNext.data = []
                this.dataPrevious.data = []
                this.loadNextGeneralMeetings();

            } else if (assemblySearch === 2) {
                
                this.dataset.filter = {name: this.dataset.search }
                this.dataPrevious.data = []
                this.dataNext.data = []
                this.loadPreviousGeneralMeetings();

            } else if (assemblySearch === '') {
                 
                this.dataset.filter = {name: this.dataset.search }
                this.dataPrevious.data = []
                this.dataNext.data = []
                this.loadNextGeneralMeetings();
                this.loadPreviousGeneralMeetings();

            } else if (assemblySearch === '' && !this.dataset.search ) {
                 
                this.loadNextGeneralMeetings();
                this.loadPreviousGeneralMeetings();
            } 

        },
    }
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.case-group {
    border: 1px solid $ui-border;
    border-radius: $ui-radius-sm;
    padding: 12px 16px;
}
</style>
